import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, FlaskConical, Landmark } from "lucide-react";
import { cachedGet } from "@/lib/api";
import { BankAvatar } from "@/components/BankAvatar";
import { PB_COST_EXAMPLE } from "@/constants/testIds";

const fmtUsd = (n) =>
  `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" });
};

const PORTFOLIO_LABELS = {
  100000: "$100K",
  1000000: "$1M",
  10000000: "$10M",
  100000000: "$100M",
};

export const PrivateBankingCostExample = () => {
  const [data, setData] = useState(null);
  const [portfolio, setPortfolio] = useState(1000000);

  useEffect(() => {
    cachedGet(`/private-banking-cost-example?portfolio_usd=${portfolio}`).then((r) => setData(r.data)).catch(() => {});
  }, [portfolio]);

  if (!data || !data.banks?.length) return null;

  return (
    <section data-testid={PB_COST_EXAMPLE.section} className="bg-[#F5F6F8] border-t border-black/[0.08] py-20 px-6 lg:px-10">
      <div className="max-w-6xl mx-auto">
        <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase font-bold text-[#059669] mb-5 border border-[#059669]/30 px-3 py-1.5">
          <FlaskConical size={12} /> Illustrative Example · Not Real-Time Data
        </span>
        <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-[#0F1115] max-w-3xl">
          What Private Banking Really Costs You?
        </h2>
        <p className="mt-5 max-w-2xl text-[#646973] leading-relaxed">
          Picture your chosen portfolio growing at a{" "}
          <span className="font-semibold text-[#0F1115]">{data.annual_return_pct}% annual return</span> over{" "}
          <span className="font-semibold text-[#0F1115]">{data.years} years</span>. Here's how the published account fee at a retail
          arm compares to the private banking arm of the same institution.
        </p>

        <div data-testid={PB_COST_EXAMPLE.scopeNote} className="mt-3 flex items-start gap-2 max-w-2xl">
          <Landmark size={13} className="text-[#646973]/70 mt-0.5 shrink-0" />
          <p className="text-xs text-[#646973]/70 leading-relaxed">
            We only list licensed retail and private banks — no asset managers, RIAs, family
            offices or fund/trust administrators — so every comparison here is bank-to-bank.
          </p>
        </div>

        <div data-testid={PB_COST_EXAMPLE.portfolioToggle} className="mt-7 inline-flex border border-black/[0.12] bg-white">
          {(data.portfolio_sizes || Object.keys(PORTFOLIO_LABELS).map(Number)).map((size) => (
            <button
              key={size}
              type="button"
              data-testid={`${PB_COST_EXAMPLE.portfolioOptionPrefix}-${size}`}
              aria-pressed={portfolio === size}
              onClick={() => setPortfolio(size)}
              className={`px-5 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                portfolio === size ? "bg-[#0F1115] text-white" : "text-[#646973] hover:text-[#0F1115]"
              }`}
            >
              {PORTFOLIO_LABELS[size] || fmtUsd(size)}
            </button>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {data.banks.map((b) => (
            <div
              key={b.label}
              data-testid={`${PB_COST_EXAMPLE.bankCardPrefix}-${b.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="bg-white border border-black/[0.08] p-6"
            >
              <div className="flex items-center gap-3">
                <BankAvatar name={b.label} size={36} />
                <div>
                  <p className="font-display font-bold text-lg text-[#0F1115]">{b.label}</p>
                  <p className="text-xs text-[#646973]">{b.country}</p>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="border border-black/[0.08] p-4">
                  <p className="text-[10px] font-bold tracking-widest uppercase text-[#646973]">Retail Banking</p>
                  <p className="text-[11px] text-[#646973] mt-1">{b.retail.name}</p>
                  <p
                    data-testid={`${PB_COST_EXAMPLE.retailTotalPrefix}-${b.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="font-display font-black text-xl text-[#0F1115] mt-3"
                  >
                    {fmtUsd(b.retail.total_10yr_usd)}
                  </p>
                  <p className="text-[10px] text-[#646973] mt-1">over {data.years} years</p>
                </div>
                <div className="border border-[#D4AF37]/40 bg-[#090A0C] p-4">
                  <p className="text-[10px] font-bold tracking-widest uppercase text-[#D4AF37]">Private Banking</p>
                  <p className="text-[11px] text-white/50 mt-1">{b.private_banking.name}</p>
                  <p
                    data-testid={`${PB_COST_EXAMPLE.pbTotalPrefix}-${b.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="font-display font-black text-xl text-[#F8FAFC] mt-3"
                  >
                    {fmtUsd(b.private_banking.total_10yr_usd)}
                  </p>
                  <p className="text-[10px] text-white/40 mt-1">over {data.years} years</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <p data-testid={PB_COST_EXAMPLE.methodologyNote} className="mt-8 max-w-3xl text-xs text-[#646973] italic leading-relaxed">
          Methodology: {data.methodology}
        </p>

        <p data-testid={PB_COST_EXAMPLE.feesAsOfNote} className="mt-2 text-xs text-[#646973]">
          These examples are based on fees as of {fmtDate(data.fees_as_of)} — the same fee data date shown across the rest of the platform.
        </p>

        <div data-testid={PB_COST_EXAMPLE.disclaimerBanner} className="mt-5 border-l-2 border-[#E52B50] pl-4">
          <p className="text-sm font-semibold text-[#0F1115]">
            These are examples only and fees may vary.
          </p>
          <p className="text-xs text-[#646973] mt-1">
            Figures are illustrative, not real-time data, and exclude transaction, FX, ATM and wire fees. They are not financial advice.
          </p>
        </div>

        <Link
          to="/private-banking/compare"
          data-testid={PB_COST_EXAMPLE.compareCta}
          className="mt-8 inline-flex items-center gap-2 bg-[#0F1115] text-white px-6 py-3 text-sm font-semibold hover:bg-[#059669] transition-colors duration-200"
        >
          Compare real Private Banking data <ArrowUpRight size={15} />
        </Link>
      </div>
    </section>
  );
};
